import styled, { keyframes } from 'styled-components';

import logo from '../../assets/logo.png';
import Button from '../ui/Button';
import { translations } from '../../data/i18n';
import { siteConfig } from '../../data/site';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 32px, 0);
    filter: blur(8px);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
    filter: blur(0);
  }
`;

const floatLogo = keyframes`
  0%, 100% {
    transform: translate3d(0, 0, 0) rotate(-1deg);
  }

  50% {
    transform: translate3d(0, -12px, 0) rotate(1deg);
  }
`;

const Section = styled.section`
  min-height: 860px;
  padding: calc(var(--nav-height) + 84px) 28px 150px;
  color: var(--white);
  position: relative;
  overflow: hidden;

  background:
    radial-gradient(circle at 72% 34%, rgba(221, 161, 47, 0.26), transparent 33%),
    radial-gradient(circle at 20% 72%, rgba(185, 43, 37, 0.13), transparent 32%),
    linear-gradient(120deg, rgba(9,8,6,1) 0%, rgba(18,15,12,0.98) 45%, rgba(44,39,32,0.92) 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      repeating-linear-gradient(
        45deg,
        rgba(255,255,255,0.032) 0,
        rgba(255,255,255,0.032) 1px,
        transparent 1px,
        transparent 18px
      );
    mask-image: linear-gradient(90deg, rgba(0,0,0,1), rgba(0,0,0,0.45));
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    right: -120px;
    top: 135px;
    width: 620px;
    height: 620px;
    background: url(${logo}) center/contain no-repeat;
    opacity: 0.035;
    filter: grayscale(1);
    pointer-events: none;
  }

  @media (max-width: 960px) {
    min-height: auto;
    padding: calc(var(--nav-height) + 62px) 22px 90px;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 2;
  max-width: var(--container);
  margin: 0 auto;

  display: grid;
  grid-template-columns: minmax(0, 1fr) 470px;
  align-items: center;
  gap: 70px;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const Content = styled.div`
  animation: ${fadeUp} 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const Eyebrow = styled.div`
  color: var(--gold-light);
  font-weight: 950;
  text-transform: uppercase;
  letter-spacing: 4px;
  margin-bottom: 20px;
  line-height: 1.45;
`;

const Title = styled.h1`
  max-width: 720px;
  margin: 0 0 30px;
  color: var(--white);
  font-size: clamp(4.2rem, 7.8vw, 8.4rem);
  line-height: 0.88;
  letter-spacing: -5px;
  text-transform: uppercase;

  @media (max-width: 760px) {
    font-size: clamp(3.2rem, 14vw, 5.4rem);
    letter-spacing: -3px;
  }
`;

const Subtitle = styled.p`
  max-width: 650px;
  margin: 0 0 34px;
  color: rgba(255,255,255,0.92);
  font-size: 1.35rem;
  line-height: 1.55;

  @media (max-width: 760px) {
    font-size: 1.08rem;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const Rating = styled.div`
  margin-top: 34px;
  display: inline-block;
  padding: 15px 18px;
  border-radius: 16px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.16);
  backdrop-filter: blur(10px);

  strong {
    display: block;
    color: var(--gold-light);
    letter-spacing: 3px;
    font-size: 1.1rem;
  }

  span {
    color: rgba(255,255,255,0.92);
  }
`;

const LogoStage = styled.div`
  position: relative;
  min-height: 540px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeUp} 1s 0.15s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 1080px) {
    min-height: 360px;
  }

  @media (max-width: 650px) {
    min-height: 280px;
  }
`;

const Glow = styled.div`
  position: absolute;
  width: 450px;
  height: 450px;
  border-radius: 999px;
  background:
    radial-gradient(circle, rgba(244,197,93,0.35), rgba(221,161,47,0.08) 46%, transparent 72%);
  filter: blur(8px);

  @media (max-width: 650px) {
    width: 310px;
    height: 310px;
  }
`;

const LogoCard = styled.div`
  position: relative;
  width: min(420px, 82vw);
  aspect-ratio: 1 / 1;
  border-radius: 36px;
  padding: 34px;

  background:
    linear-gradient(145deg, rgba(255,248,236,0.96), rgba(255,255,255,0.82)),
    radial-gradient(circle at top left, rgba(244,197,93,0.35), transparent 46%);

  box-shadow:
    0 34px 90px rgba(0,0,0,0.38),
    inset 0 0 0 1px rgba(255,255,255,0.75);

  animation: ${floatLogo} 5.5s ease-in-out infinite;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &::before {
    content: '';
    position: absolute;
    inset: -16px;
    border-radius: 44px;
    border: 1px solid rgba(244,197,93,0.28);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    right: -26px;
    bottom: 42px;
    width: 150px;
    height: 150px;
    border-radius: 999px;
    background:
      conic-gradient(
        from 0deg,
        var(--green),
        var(--gold),
        var(--red),
        var(--black),
        var(--green)
      );
    opacity: 0.22;
    filter: blur(1px);
    z-index: -1;
  }

  @media (max-width: 650px) {
    width: min(290px, 78vw);
    border-radius: 28px;
    padding: 24px;
  }
`;

const StudioBadge = styled.div`
  position: absolute;
  left: 12px;
  bottom: 32px;
  max-width: 330px;
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(9,8,6,0.78);
  border: 1px solid rgba(244,197,93,0.28);
  box-shadow: 0 18px 40px rgba(0,0,0,0.32);
  backdrop-filter: blur(16px);

  strong {
    display: block;
    color: var(--gold-light);
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: 0.95rem;
    margin-bottom: 7px;
  }

  span {
    color: rgba(255,255,255,0.88);
    line-height: 1.45;
  }

  @media (max-width: 650px) {
    display: none;
  }
`;

function Hero({ language }) {
  const t = translations[language].hero;

  return (
    <Section id="home">
      <Inner>
        <Content>
          <Eyebrow>{siteConfig.brandName} · {t.eyebrow}</Eyebrow>

          <Title>{t.title}</Title>

          <Subtitle>{t.subtitle}</Subtitle>

          <Actions>
            <Button href="#booking">📅 {t.primary}</Button>
            <Button href="#gallery" $variant="outline">{t.secondary} →</Button>
          </Actions>

          <Rating>
            <strong>★★★★★</strong>
            <span>{t.rating}</span>
          </Rating>
        </Content>

        <LogoStage aria-hidden="true">
          <Glow />
          <LogoCard>
            <img src={logo} alt="" />
          </LogoCard>

          <StudioBadge>
            <strong>{t.badgeTitle}</strong>
            <span>{t.badgeText}</span>
          </StudioBadge>
        </LogoStage>
      </Inner>
    </Section>
  );
}

export default Hero;